import { useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const MENU_BY_PERIOD = {
  morning: [
    ['today', 'Hoje', '◷'],
    ['calendar', 'Agenda', '◫'],
    ['routine', 'Rotina', '◌'],
    ['tasks', 'Tarefas', '✓'],
    ['chat', 'Conversar', '◉'],
    ['capture', 'Capturar', '+'],
  ],
  work: [
    ['projects', 'Projetos', '▣'],
    ['capture', 'Capturar', '+'],
    ['vault', 'Vault', '◇'],
    ['today', 'Hoje', '◷'],
    ['tasks', 'Tarefas', '✓'],
    ['chat', 'Conversar', '◉'],
  ],
  night: [
    ['today', 'Hoje', '◷'],
    ['tasks', 'Pendências', '✓'],
    ['routine', 'Rotina', '◌'],
    ['capture', 'Salvar', '+'],
    ['vault', 'Vault', '◇'],
    ['chat', 'Conversar', '◉'],
  ],
};

function periodFor(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return 'morning';
  if (hour < 19) return 'work';
  return 'night';
}

/**
 * A discoverable, keyboard-accessible alternative to a hidden gesture. It
 * intentionally carries navigation/capture names only, never private item
 * details from the current timeline or project.
 */
export function CoreRadialMenu({ open, onClose, onAction, period }) {
  const menuPeriod = period || periodFor();
  const items = useMemo(() => MENU_BY_PERIOD[menuPeriod] || MENU_BY_PERIOD.work, [menuPeriod]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          aria-label="Menu da Athena"
          aria-modal="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          style={{
            position: 'fixed', inset: 0, zIndex: 90, display: 'grid', placeItems: 'center',
            background: 'radial-gradient(circle at 50% 50%, rgba(11,30,61,.28), rgba(1,4,14,.62) 66%, rgba(1,4,14,.74))',
            backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)',
          }}
          onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}
        >
          <div role="menu" aria-label="Ações rápidas" style={{ position: 'relative', width: 'min(88vw, 430px)', height: 'min(88vw, 430px)', maxHeight: '72dvh' }}>
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.78, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              style={{ position: 'absolute', inset: '32%', borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'radial-gradient(circle, rgba(20,115,211,.35), rgba(9,34,78,.82) 58%, rgba(2,8,24,.92))', border: '1px solid rgba(104,212,255,.38)', boxShadow: '0 0 50px rgba(22,152,255,.2), inset 0 0 30px rgba(38,208,255,.12)' }}
              aria-hidden="true"
            >
              <span style={{ color: 'rgba(226,250,255,.92)', fontSize: 12, fontWeight: 650, letterSpacing: '.12em', textTransform: 'uppercase' }}>Athena</span>
            </motion.div>
            {items.map(([id, label, glyph], index) => {
              const angle = -90 + index * (360 / items.length);
              const radians = (angle * Math.PI) / 180;
              const distance = 39;
              const left = 50 + Math.cos(radians) * distance;
              const top = 50 + Math.sin(radians) * distance;
              return (
                <motion.button
                  key={id}
                  type="button"
                  role="menuitem"
                  aria-label={label}
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.025 * index, type: 'spring', stiffness: 320, damping: 22 }}
                  onClick={() => onAction?.(id)}
                  style={{
                    position: 'absolute', left: `${left}%`, top: `${top}%`, transform: 'translate(-50%, -50%)',
                    minWidth: 72, minHeight: 72, padding: '8px 9px', borderRadius: 18, border: '1px solid rgba(121,199,255,.28)',
                    color: '#e7f5ff', background: 'linear-gradient(145deg, rgba(13,39,78,.96), rgba(5,17,40,.96))',
                    boxShadow: '0 10px 28px rgba(0,0,0,.28)', cursor: 'pointer', display: 'grid', placeItems: 'center', gap: 3,
                  }}
                >
                  <span aria-hidden="true" style={{ color: '#70d8ff', fontSize: 21, lineHeight: 1 }}>{glyph}</span>
                  <span style={{ fontSize: 10, fontWeight: 650, whiteSpace: 'nowrap' }}>{label}</span>
                </motion.button>
              );
            })}
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar menu da Athena"
              style={{ position: 'absolute', left: '50%', bottom: -10, transform: 'translateX(-50%)', minHeight: 44, padding: '10px 14px', borderRadius: 22, border: '1px solid rgba(180,220,255,.24)', color: 'rgba(233,247,255,.9)', background: 'rgba(6,18,42,.92)', cursor: 'pointer', fontSize: 12 }}
            >
              Fechar
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CoreRadialMenu;
